/**
 * GuardDutyAlert.js — CẢNH BÁO ĐỎ CHO NGƯỜI TRỰC VỌNG GÁC (GUARD24).
 *
 * Người trực vọng gác đều là role EMPLOYEE ⇒ họ sống ở /portal và /profile, không bao
 * giờ vào Dashboard. Trước đây không màn hình nào nói với họ rằng phiên trực của mình
 * đang treo — Lê Chí Trung có 10 phiên mở từ 23/07, phiên cũ nhất hơn 1000 giờ, mà
 * không hề hay biết. Banner này LUÔN hiện cho người thuộc diện vọng gác (kể cả khi
 * chưa có sự cố) để họ nắm luật; khi có phiên treo / OT bị chặn thì chuyển sang đỏ đậm.
 *
 *   <GuardDutyAlert />        — người trực tự xem (gọi /attendance/guard-alert/me)
 *   <GuardDutyAlertAdmin />   — quản lý xem toàn bộ người trực (/attendance/guard-alert/all)
 */
import React, { useEffect, useState } from 'react';
import { ShieldAlert, Clock, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import api from '../../services/api';

const fmtH = (h) => {
  const n = Number(h) || 0;
  if (n < 24) return `${n}h`;
  const d = Math.floor(n / 24);
  const rem = Math.round(n % 24);
  return rem > 0 ? `${d} ngày ${rem}h` : `${d} ngày`;
};

export function GuardDutyAlert() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/attendance/guard-alert/me');
        if (alive) setData(res.data?.data || null);
      } catch (_) { if (alive) setData(null); }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!data?.is_guard) return null; // không thuộc diện vọng gác → không hiện gì

  const danger = data.level === 'danger';
  const cur = data.current_session;

  return (
    <div className={`rounded-2xl border-2 overflow-hidden ${danger
      ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20'
      : 'border-rose-300 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-900/10'}`}>
      <div className="px-4 py-3 flex items-start gap-3">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${danger
          ? 'bg-rose-600 text-white' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300'}`}>
          <ShieldAlert className="w-5 h-5" strokeWidth={2.2} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-rose-800 dark:text-rose-200 leading-tight">
            {danger ? '⚠ TRỰC VỌNG GÁC — CẦN XỬ LÝ NGAY' : 'Bạn thuộc diện trực vọng gác 24h'}
          </p>

          {/* Phiên đang trực */}
          {cur && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`text-[11px] font-semibold px-2 py-1 rounded-lg inline-flex items-center gap-1 ${cur.over_standard
                ? 'bg-rose-600 text-white'
                : 'bg-white text-rose-700 dark:bg-gray-800 dark:text-rose-300 border border-rose-200 dark:border-rose-800'}`}>
                <Clock className="w-3.5 h-3.5" />
                Đang trực từ {cur.check_in_text} — {fmtH(cur.hours_open)}/24h
                {cur.over_standard && ` (VƯỢT ${fmtH(cur.overdue_hours)})`}
              </span>
              {cur.over_standard && data.allow_handoff && (
                <span className="text-[11px] font-semibold px-2 py-1 rounded-lg bg-white dark:bg-gray-800 text-rose-700 dark:text-rose-300 border border-rose-300 inline-flex items-center gap-1">
                  <ArrowRightLeft className="w-3.5 h-3.5" /> Hãy bàn giao ca ngay
                </span>
              )}
            </div>
          )}

          {/* Phiên treo + OT bị chặn */}
          {(data.stale_count > 0 || data.blocked_ot?.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {data.stale_count > 0 && (
                <span className="text-[11px] font-bold px-2 py-1 rounded-lg bg-rose-600 text-white inline-flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {data.stale_count} phiên trực CŨ chưa kết thúc
                </span>
              )}
              {data.blocked_ot?.length > 0 && (
                <span className="text-[11px] font-bold px-2 py-1 rounded-lg bg-amber-500 text-white inline-flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {data.blocked_ot.length} phiếu OT ({data.blocked_ot_hours}h) — KHÔNG được duyệt
                </span>
              )}
            </div>
          )}

          <ul className="mt-2 space-y-1">
            {data.messages.map((m, i) => (
              <li key={i} className="text-[11px] text-rose-800/90 dark:text-rose-200/90 leading-snug flex gap-1.5">
                <span className="text-rose-400 flex-shrink-0">•</span><span>{m}</span>
              </li>
            ))}
          </ul>

          {/* Danh sách phiên treo — nói rõ ngày nào, mở bao lâu, để đi báo HR cho đúng */}
          {data.stale_count > 0 && (
            <details className="mt-2">
              <summary className="text-[11px] font-semibold text-rose-700 dark:text-rose-300 cursor-pointer select-none">
                Xem {data.stale_count} phiên trực chưa kết thúc
              </summary>
              <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                {data.stale_sessions.map((s) => (
                  <li key={s.attendance_id} className="text-[11px] text-rose-800 dark:text-rose-200 flex items-center gap-2">
                    <span className="font-mono">{s.check_in_text}</span>
                    <span className="text-rose-500">— đã mở {fmtH(s.hours_open)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

/** Bản cho quản lý: gộp mọi người trực, chỉ hiện khi có ít nhất 1 người có sự cố. */
export function GuardDutyAlertAdmin() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/attendance/guard-alert/all');
        if (alive) setRows(res.data?.data || []);
      } catch (_) { if (alive) setRows([]); }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const problems = rows.filter((r) => r.level === 'danger');
  if (!problems.length) return null;

  return (
    <div className="rounded-2xl border-2 border-rose-500 bg-rose-50 dark:bg-rose-900/20 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2.5 border-b border-rose-200 dark:border-rose-800">
        <span className="w-9 h-9 rounded-xl bg-rose-600 text-white flex items-center justify-center">
          <ShieldAlert className="w-5 h-5" strokeWidth={2.2} />
        </span>
        <div>
          <p className="text-sm font-bold text-rose-800 dark:text-rose-200 leading-tight">
            ⚠ Trực vọng gác có sự cố · {problems.length} người
          </p>
          <p className="text-[11px] text-rose-700/80 dark:text-rose-300/80">
            Phiên trực chưa kết thúc thì giờ công chưa được chốt. OT vọng gác KHÔNG duyệt được — phải chốt phiên trước.
          </p>
        </div>
      </div>
      <ul className="divide-y divide-rose-200/60 dark:divide-rose-800/60">
        {problems.map((r) => (
          <li key={r.employee_id} className="px-4 py-2.5 flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{r.full_name}</span>
            {r.current_session && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.current_session.over_standard
                ? 'bg-rose-600 text-white' : 'bg-white dark:bg-gray-800 text-rose-700 dark:text-rose-300 border border-rose-200'}`}>
                đang trực {fmtH(r.current_session.hours_open)}/24h
              </span>
            )}
            {r.stale_count > 0 && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white">
                {r.stale_count} phiên treo
              </span>
            )}
            {r.blocked_ot?.length > 0 && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                {r.blocked_ot.length} phiếu OT {r.blocked_ot_hours}h — không duyệt
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default GuardDutyAlert;
